import React from 'react';
import PropTypes from 'prop-types';
// material
import { styled, useTheme } from '@mui/material/styles';
import { Button, Grid, Typography, IconButton, Box } from '@mui/material';
import { CarouselProvider, Slider, Slide, ButtonBack, ButtonNext } from 'pure-react-carousel';
import 'pure-react-carousel/dist/react-carousel.es.css';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
// icons
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
// component
import BaseDialog from '../base-dialog';
import { ReactComponent as PhotoPlaceholder } from '../../assets/ic_photo_placeholder.svg';

const ButtonContainer = styled(Grid)(() => ({
    marginTop: 20,
}));

const SlideStyle = styled(Slide)(() => ({
    'div': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    'video': {
        maxWidth: 500,
        minWidth: 500
    },
    'img': {
        maxWidth: 500,
        minWidth: 500
    }
}));

const PhotoPlaceholderStyle = styled(PhotoPlaceholder)(({ theme }) => ({
    margin: 20,
    width: 250,
    height: 250,
    'path': {
        fill: theme.palette.grey[600],
    }
}));

const ZoomControls = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    zIndex: 1000,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderRadius: theme.spacing(1),
    padding: theme.spacing(0.5),
    display: 'flex',
    gap: theme.spacing(0.5),
    '& .MuiIconButton-root': {
        backgroundColor: '#fff',
        '&:hover': {
            backgroundColor: '#f5f5f5',
        },
    },
}));

MediaCarouselDialog.propTypes = {
    files: PropTypes.array,
    titles: PropTypes.object,
    open: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
};

// TODO - IMPLEMENT AND TEST VIDEO FILES

// Documentation for the 'pure-react-carousel' material can be viewed on this
// link: https://www.npmjs.com/package/pure-react-carousel?activeTab=readme
export default function MediaCarouselDialog({ files, titles, open, onClose }) {
    const theme = useTheme();

    return (
        <BaseDialog
            title={titles.title}
            open={open}
            onClose={onClose}
            hideActions
        >
            <CarouselProvider
                naturalSlideWidth={150}
                naturalSlideHeight={150}
                totalSlides={files?.length}
                infinite
            >
                <Slider>
                    {files?.map((media, index) => (
                        <SlideStyle key={index} index={index}>
                            {getMediaType(media, titles.invalid, theme, index)}
                        </SlideStyle>
                    ))}
                </Slider>
                <ButtonContainer container justifyContent="space-between">
                    <ButtonBack style={{ border: 0, borderRadius: 10 }}>
                        <Button variant="contained">
                            {titles.back}
                        </Button>
                    </ButtonBack>
                    <ButtonNext style={{ border: 0, borderRadius: 10 }}>
                        <Button variant="contained">
                            {titles.next}
                        </Button>
                    </ButtonNext>
                </ButtonContainer>
            </CarouselProvider>
        </BaseDialog>
    );
};

// This method receives a url (of type file) and 
// determines what type of media the url is (audio, 
// video, image), returning the correct component 
// for display. 
// The object 'type' contains 3 different keys corresponding 
// to the type of file we are expecting to receive. Each key 
// contains 10 types of extensions that we can receive from 
// each category of file. The 10 in each category were taken 
// from ChatGPT's answer to the question "10 extensions more
// used by (...)" them the respective category.
export function getMediaType(url, message, theme, index) {
    const types = {
        video: ['mp4', 'mov', 'avi', 'mkv', 'wmv', 'flv', 'mpeg', 'webm', '3gp', 'ogv'],
        audio: ['mp3', 'wav', 'ogg', 'aac', 'flac', 'wma', 'm4a', 'mp2', 'ac3', 'ra'],
        imagem: ['jpg', 'jpeg', 'png', 'eps', 'bmp', 'tiff', 'webp', 'raw', 'psd', 'ai']
    }

    if (typeof url === 'string' && url !== null) {
        const extension = url.split('.').pop().toLowerCase();

        if (types.video.includes(extension)) {
            return (
                <video controls>
                    <source src={url} type={`video/${extension}`} />
                    <track kind="captions" />
                </video>
            );
        }

        if (types.audio.includes(extension)) {
            return (
                <audio controls>
                    <source src={url} type={`audio/${extension}`} />
                    <track kind="captions" />
                </audio>
            );
        }

        // Default to images with zoom functionality
        return (
            <Box style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <TransformWrapper
                    key={`transform-${index}`}
                    initialScale={1}
                    initialPositionX={0}
                    initialPositionY={0}
                    minScale={0.5}
                    maxScale={4}
                    wheel={{ step: 0.1 }}
                    doubleClick={{ mode: 'toggle' }}
                    panning={{ disabled: false }}
                    centerOnInit
                >
                    {({ zoomIn, zoomOut, resetTransform }) => (
                        <>
                            <ZoomControls>
                                <IconButton
                                    onClick={() => zoomIn()}
                                    size="small"
                                    title="Zoom in"
                                    sx={{ color: theme?.palette?.primary?.main }}
                                >
                                    <ZoomInIcon />
                                </IconButton>
                                <IconButton
                                    onClick={() => zoomOut()}
                                    size="small"
                                    title="Zoom out"
                                    sx={{ color: theme?.palette?.primary?.main }}
                                >
                                    <ZoomOutIcon />
                                </IconButton>
                                <IconButton
                                    onClick={() => resetTransform()}
                                    size="small"
                                    title="Reset"
                                    sx={{ color: theme?.palette?.primary?.main }}
                                >
                                    <RestartAltIcon />
                                </IconButton>
                            </ZoomControls>
                            <TransformComponent>
                                <img src={url} alt="img" style={{ minWidth: 250, maxWidth: 550 }} />
                            </TransformComponent>
                        </>
                    )}
                </TransformWrapper>
            </Box>
        );
    }
    return <InvalidAttachment message={message} />;
};

InvalidAttachment.propTypes = {
    message: PropTypes.string,
}

function InvalidAttachment({ message }) {
    return (
        <Grid container direction="column" spacing={2}>
            <Grid item>
                <PhotoPlaceholderStyle />
            </Grid>
            <Grid item>
                <Typography>{message}</Typography>
            </Grid>
        </Grid>
    );
};